import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  PenSquare, FileText, Eye, EyeOff, Trash2, Mail, MailOpen,
  LogOut, Cloud, CloudOff, Plus, ExternalLink, Users, BarChart3, Palette
} from 'lucide-react';
import AnalyticsTab from './AnalyticsTab';
import EmailSettingsTab from './EmailSettingsTab';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [driveStatus, setDriveStatus] = useState({ connected: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    
    // Check for Dropbox connection status
    if (searchParams.get('dropbox_connected') === 'true') {
      toast.success('Dropbox connected successfully!');
    } else if (searchParams.get('dropbox_error')) {
      toast.error(`Dropbox connection failed: ${searchParams.get('dropbox_error')}`);
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, postsRes, contactsRes, dropboxRes, subsRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers }),
        axios.get(`${API}/posts/admin`, { headers }),
        axios.get(`${API}/contacts`, { headers }),
        axios.get(`${API}/dropbox/status`, { headers }),
        axios.get(`${API}/subscribers`, { headers }),
      ]);
      setStats(statsRes.data);
      setPosts(postsRes.data);
      setContacts(contactsRes.data);
      setDriveStatus(dropboxRes.data);
      setSubscribers(subsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await axios.delete(`${API}/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Post deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      await axios.put(
        `${API}/posts/${post.id}`,
        { published: !post.published },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(post.published ? 'Post unpublished' : 'Post published');
      fetchData();
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const handleMarkRead = async (contactId) => {
    try {
      await axios.put(
        `${API}/contacts/${contactId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await axios.delete(`${API}/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Contact deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleConnectDrive = async () => {
    try {
      const response = await axios.get(`${API}/dropbox/connect`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Dropbox');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-800"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-serif text-xl font-bold text-stone-900 tracking-tighter">
              Whispers of Nepal
            </Link>
            <Badge variant="secondary" className="bg-stone-100 text-stone-600">
              Dashboard
            </Badge>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-stone-600 hidden md:block">
              Welcome, {user?.name}
            </span>
            <Link
              to="/"
              className="text-stone-600 hover:text-stone-900 transition-colors"
              data-testid="view-site-link"
            >
              <ExternalLink size={18} />
            </Link>
            <button
              onClick={handleLogout}
              className="text-stone-600 hover:text-red-800 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white p-6 rounded-sm border border-stone-200" data-testid="stat-total-posts">
            <p className="text-sm text-stone-500 mb-1">Total Posts</p>
            <p className="text-3xl font-serif text-stone-900">{stats?.total_posts || 0}</p>
          </div>
          <div className="bg-white p-6 rounded-sm border border-stone-200" data-testid="stat-published">
            <p className="text-sm text-stone-500 mb-1">Published</p>
            <p className="text-3xl font-serif text-green-700">{stats?.published_posts || 0}</p>
          </div>
          <div className="bg-white p-6 rounded-sm border border-stone-200" data-testid="stat-drafts">
            <p className="text-sm text-stone-500 mb-1">Drafts</p>
            <p className="text-3xl font-serif text-amber-600">{stats?.draft_posts || 0}</p>
          </div>
          <div className="bg-white p-6 rounded-sm border border-stone-200" data-testid="stat-subscribers">
            <p className="text-sm text-stone-500 mb-1">Subscribers</p>
            <p className="text-3xl font-serif text-blue-700">{stats?.total_subscribers || 0}</p>
          </div>
          <div className="bg-white p-6 rounded-sm border border-stone-200" data-testid="stat-messages">
            <p className="text-sm text-stone-500 mb-1">Messages</p>
            <p className="text-3xl font-serif text-stone-900">
              {stats?.total_contacts || 0}
              {stats?.unread_contacts > 0 && (
                <span className="text-sm text-red-800 ml-2">({stats.unread_contacts} new)</span>
              )}
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-4 mb-8">
          <Link
            to="/admin/posts/new"
            className="inline-flex items-center gap-2 bg-stone-900 text-stone-50 px-6 py-3 rounded-sm hover:bg-red-800 transition-colors"
            data-testid="new-post-btn"
          >
            <Plus size={18} /> New Story
          </Link>
          <button
            onClick={handleConnectDrive}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-sm border transition-colors ${
              driveStatus.connected
                ? 'border-green-300 text-green-700 bg-green-50'
                : driveStatus.expired
                ? 'border-amber-300 text-amber-700 bg-amber-50'
                : 'border-stone-300 text-stone-700 hover:bg-stone-50'
            }`}
            data-testid="connect-drive-btn"
          >
            {driveStatus.connected ? (
              <>
                <Cloud size={18} /> Dropbox Connected
              </>
            ) : driveStatus.expired ? (
              <>
                <CloudOff size={18} /> Reconnect Dropbox (Expired)
              </>
            ) : (
              <>
                <CloudOff size={18} /> Connect Dropbox
              </>
            )}
          </button>
        </div>

        {/* Content Tabs */}
        <Tabs defaultValue="posts" className="w-full">
          <TabsList className="bg-white border border-stone-200 mb-6">
            <TabsTrigger value="posts" className="flex items-center gap-2" data-testid="posts-tab">
              <FileText size={16} /> Posts
            </TabsTrigger>
            <TabsTrigger value="messages" className="flex items-center gap-2" data-testid="messages-tab">
              <Mail size={16} /> Messages
              {stats?.unread_contacts > 0 && (
                <Badge variant="destructive" className="ml-1">{stats.unread_contacts}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="subscribers" className="flex items-center gap-2" data-testid="subscribers-tab">
              <Users size={16} /> Subscribers
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2" data-testid="analytics-tab-trigger">
              <BarChart3 size={16} /> Analytics
            </TabsTrigger>
            <TabsTrigger value="email-settings" className="flex items-center gap-2" data-testid="email-settings-tab-trigger">
              <Palette size={16} /> Email Design
            </TabsTrigger>
          </TabsList>

          {/* Posts Tab */}
          <TabsContent value="posts">
            <div className="bg-white rounded-sm border border-stone-200">
              {posts.length === 0 ? (
                <div className="p-12 text-center" data-testid="no-posts">
                  <FileText size={40} className="text-stone-300 mx-auto mb-4" />
                  <h3 className="font-serif text-xl text-stone-900 mb-2">No stories yet</h3>
                  <p className="text-stone-600 mb-6">Start writing your first story</p>
                  <Link
                    to="/admin/posts/new"
                    className="inline-flex items-center gap-2 bg-stone-900 text-stone-50 px-6 py-3 rounded-sm hover:bg-red-800 transition-colors"
                  >
                    <Plus size={18} /> Create Story
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {posts.map((post) => (
                    <div
                      key={post.id}
                      className="p-6 flex items-center justify-between gap-4 hover:bg-stone-50 transition-colors"
                      data-testid={`post-row-${post.id}`}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        {post.featured_image && (
                          <div className="w-16 h-12 rounded-sm overflow-hidden flex-shrink-0">
                            <img
                              src={post.featured_image}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="min-w-0">
                          <h3 className="font-medium text-stone-900 truncate">{post.title}</h3>
                          <div className="flex items-center gap-3 mt-1">
                            <Badge
                              variant={post.published ? 'default' : 'secondary'}
                              className={post.published ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}
                            >
                              {post.published ? 'Published' : 'Draft'}
                            </Badge>
                            <span className="text-xs text-stone-500">
                              {format(new Date(post.created_at), 'MMM d, yyyy')}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleTogglePublish(post)}
                          className="p-2 text-stone-500 hover:text-stone-900 transition-colors"
                          title={post.published ? 'Unpublish' : 'Publish'}
                          data-testid={`toggle-publish-${post.id}`}
                        >
                          {post.published ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                        <Link
                          to={`/admin/posts/${post.id}/edit`}
                          className="p-2 text-stone-500 hover:text-stone-900 transition-colors"
                          data-testid={`edit-post-${post.id}`}
                        >
                          <PenSquare size={18} />
                        </Link>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button
                              className="p-2 text-stone-500 hover:text-red-800 transition-colors"
                              data-testid={`delete-post-${post.id}`}
                            >
                              <Trash2 size={18} />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this story?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This action cannot be undone. The story will be permanently deleted.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeletePost(post.id)}
                                className="bg-red-800 hover:bg-red-900"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <div className="bg-white rounded-sm border border-stone-200">
              {contacts.length === 0 ? (
                <div className="p-12 text-center" data-testid="no-messages">
                  <Mail size={40} className="text-stone-300 mx-auto mb-4" />
                  <h3 className="font-serif text-xl text-stone-900 mb-2">No messages yet</h3>
                  <p className="text-stone-600">Messages from editors will appear here</p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className={`p-6 ${!contact.read ? 'bg-amber-50/50' : ''}`}
                      data-testid={`contact-row-${contact.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {contact.read ? (
                              <MailOpen size={18} className="text-stone-400" />
                            ) : (
                              <Mail size={18} className="text-amber-600" />
                            )}
                            <span className="font-medium text-stone-900">{contact.name}</span>
                            <span className="text-stone-500 text-sm">{contact.email}</span>
                          </div>
                          {contact.company && (
                            <p className="text-sm text-stone-600 mb-2">{contact.company}</p>
                          )}
                          {contact.subject && (
                            <p className="font-medium text-stone-800 mb-2">{contact.subject}</p>
                          )}
                          <p className="text-stone-600 whitespace-pre-wrap">{contact.message}</p>
                          <p className="text-xs text-stone-400 mt-3">
                            {format(new Date(contact.created_at), 'MMM d, yyyy h:mm a')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {!contact.read && (
                            <button
                              onClick={() => handleMarkRead(contact.id)}
                              className="p-2 text-stone-500 hover:text-stone-900 transition-colors"
                              title="Mark as read"
                              data-testid={`mark-read-${contact.id}`}
                            >
                              <MailOpen size={18} />
                            </button>
                          )}
                          <a
                            href={`mailto:${contact.email}`}
                            className="p-2 text-stone-500 hover:text-stone-900 transition-colors"
                            title="Reply"
                          >
                            <Mail size={18} />
                          </a>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button
                                className="p-2 text-stone-500 hover:text-red-800 transition-colors"
                                data-testid={`delete-contact-${contact.id}`}
                              >
                                <Trash2 size={18} />
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete this message?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteContact(contact.id)}
                                  className="bg-red-800 hover:bg-red-900"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Subscribers Tab */}
          <TabsContent value="subscribers">
            <div className="bg-white rounded-sm border border-stone-200">
              {subscribers.length === 0 ? (
                <div className="p-12 text-center" data-testid="no-subscribers">
                  <Users size={40} className="text-stone-300 mx-auto mb-4" />
                  <h3 className="font-serif text-xl text-stone-900 mb-2">No subscribers yet</h3>
                  <p className="text-stone-600">People can subscribe from the footer to get notified about new posts</p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {subscribers.map((sub) => (
                    <div
                      key={sub.id}
                      className={`p-6 flex items-center justify-between ${!sub.active ? 'opacity-50' : ''}`}
                      data-testid={`subscriber-row-${sub.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center">
                          <Mail size={18} className="text-stone-500" />
                        </div>
                        <div>
                          <p className="font-medium text-stone-900">{sub.email}</p>
                          {sub.name && <p className="text-sm text-stone-500">{sub.name}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge variant={sub.active ? 'default' : 'secondary'} className={sub.active ? 'bg-green-100 text-green-700' : ''}>
                          {sub.active ? 'Active' : 'Unsubscribed'}
                        </Badge>
                        <span className="text-xs text-stone-500">
                          {format(new Date(sub.subscribed_at), 'MMM d, yyyy')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <AnalyticsTab token={token} />
          </TabsContent>

          {/* Email Settings Tab */}
          <TabsContent value="email-settings">
            <EmailSettingsTab token={token} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
